import { mergeAttributes, Node } from '@tiptap/core';
import type { MarkType, NodeType, Node as PMNode, ResolvedPos } from '@tiptap/pm/model';
import { Plugin, TextSelection, type Transaction } from '@tiptap/pm/state';
import { AddMarkStep } from '@tiptap/pm/transform';
import { ReactNodeViewRenderer } from '@tiptap/react';

import {
  afterTrailingNodes,
  appendBelowLinkPrompt,
  type LinkPromptState,
  linkPromptKey,
  replaceLinkPrompt,
} from '../LinkPrompt/LinkPrompt';
import { vendors as linkEmbedVendors } from '../LinkPrompt/vendors/registry';
import { LinkCardView } from './LinkCardView';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    linkCard: {
      insertLinkCard: (url: string, options?: { manual?: boolean }) => ReturnType;
      showLinkPromptPreview: () => ReturnType;
      appendLinkPromptPreview: () => ReturnType;
    };
  }
}

export interface LinkPreviewMetadata {
  title?: string;
  description?: string;
  image?: string;
  siteName?: string;
}

export interface LinkCardOptions {
  onFetchLinkPreview?: (url: string) => Promise<LinkPreviewMetadata | null>;
  translations?: Record<string, string>;
}

export const isSingleUrl = (text: string): boolean => {
  if (/\s/.test(text)) return false;
  try {
    const url = new URL(text);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const urlTokens = (text: string): Array<{ offset: number; url: string }> => {
  const tokens: Array<{ offset: number; url: string }> = [];
  for (const match of text.matchAll(/\S+/g)) {
    if (match.index !== undefined && isSingleUrl(match[0])) tokens.push({ offset: match.index, url: match[0] });
  }
  return tokens;
};

const MAX_LINK_CARDS = 3;

const countLinkCards = (doc: PMNode, type: NodeType): number => {
  let total = 0;
  doc.descendants((node) => {
    if (node.type === type) total += 1;
  });
  return total;
};

const insertCardAfterBlock = (
  tr: Transaction,
  type: NodeType,
  url: string,
  $pos: ResolvedPos,
  manual = false,
): Transaction => {
  let exists = false;
  let total = 0;
  tr.doc.descendants((node) => {
    if (node.type !== type) return;
    total += 1;
    if (node.attrs.url === url) exists = true;
  });
  if (exists || total >= MAX_LINK_CARDS) return tr;

  const cardNode = type.create({ url, loading: true, manual });

  if (manual && $pos.depth >= 1) {
    const block = $pos.node(1);
    if (block.type.name === 'paragraph' && block.textContent === '') {
      const beforeBlock = $pos.before(1);
      const inserted = tr.insert(beforeBlock, cardNode);
      return inserted.setSelection(TextSelection.create(inserted.doc, beforeBlock + cardNode.nodeSize + 1));
    }
  }

  return tr.insert(afterTrailingNodes(tr, type, $pos.after(1)), cardNode);
};

// An unmarked space already there separates the link on its own — adding the
// separator would only double it.
const isPlainSpaceAt = (tr: Transaction, pos: number, linkMark: MarkType): boolean =>
  tr.doc.textBetween(pos, Math.min(pos + 1, tr.doc.content.size)) === ' ' &&
  !linkMark.isInSet(tr.doc.resolve(pos + 1).marks());

const blockHasLink = (block: PMNode, linkMark: MarkType): boolean => {
  let found = false;
  block.descendants((child) => {
    if (linkMark.isInSet(child.marks)) found = true;
  });
  return found;
};

const liveHrefs = (doc: PMNode, linkMark: MarkType): Set<string> => {
  const hrefs = new Set<string>();
  doc.descendants((node) => {
    for (const mark of node.marks) {
      if (mark.type === linkMark && typeof mark.attrs.href === 'string') hrefs.add(mark.attrs.href);
    }
  });
  return hrefs;
};

export const LinkCard = Node.create<LinkCardOptions>({
  name: 'linkCard',
  group: 'block',
  atom: true,
  draggable: false,
  selectable: true,

  addOptions() {
    return { onFetchLinkPreview: undefined, translations: undefined };
  },

  addAttributes() {
    return {
      url: { default: null },
      title: { default: null },
      description: { default: null },
      image: { default: null },
      siteName: { default: null },
      loading: { default: false },
      manual: { default: false },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-link-card]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-link-card': '' })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(LinkCardView);
  },

  addCommands() {
    const onFetch = this.options.onFetchLinkPreview;
    const type = this.type;

    return {
      insertLinkCard:
        (url: string, options?: { manual?: boolean }) =>
        ({ tr, dispatch }) => {
          if (!onFetch) return true;
          if (dispatch) insertCardAfterBlock(tr, type, url, tr.selection.$from, options?.manual ?? false);
          return true;
        },
      // "Preview only": replace the pasted link text with its card.
      showLinkPromptPreview: () => (props) => {
        const { state, tr, dispatch } = props;
        const prompt = linkPromptKey.getState(state);
        if (!prompt) return false;

        // Nothing to show (no fetch handler, or at the card cap): keep the link.
        // No URL dedupe here — an explicit "Preview only" choice always gets its card.
        if (!onFetch || countLinkCards(state.doc, type) >= MAX_LINK_CARDS) {
          if (dispatch) tr.setMeta(linkPromptKey, null);
          return true;
        }

        const url = state.doc.textBetween(prompt.from, prompt.to);
        // manual: the link text is consumed, so orphan cleanup must not reap the card.
        return replaceLinkPrompt(props, prompt, {
          type: type.name,
          attrs: { url, loading: true, manual: true },
        });
      },
      // "Preview + URL": keep the link text and add its card after the block.
      appendLinkPromptPreview: () => (props) => {
        const { state } = props;
        const prompt = linkPromptKey.getState(state);
        if (!prompt) return false;

        const card =
          onFetch && countLinkCards(state.doc, type) < MAX_LINK_CARDS
            ? // manual: an explicit choice — deleting the link later must not reap the card.
              type.create({ url: state.doc.textBetween(prompt.from, prompt.to), loading: true, manual: true })
            : null;
        return appendBelowLinkPrompt(props, prompt, card);
      },
    };
  },

  addProseMirrorPlugins() {
    const onFetch = this.options.onFetchLinkPreview;
    const type = this.type;

    return [
      new Plugin({
        appendTransaction: (transactions, _oldState, newState) => {
          if (!onFetch || !transactions.some((t) => t.docChanged)) return null;
          const linkMark = newState.schema.marks.link;
          if (!linkMark) return null;

          let tr = newState.tr;

          const hrefs = liveHrefs(newState.doc, linkMark);
          const orphans: Array<{ from: number; to: number }> = [];
          newState.doc.forEach((node, offset) => {
            if (
              node.type === type &&
              node.attrs.manual !== true &&
              typeof node.attrs.url === 'string' &&
              !hrefs.has(node.attrs.url)
            ) {
              orphans.push({ from: offset, to: offset + node.nodeSize });
            }
          });
          for (let i = orphans.length - 1; i >= 0; i--) {
            tr = tr.delete(orphans[i].from, orphans[i].to);
          }

          const addedLink = transactions.some((t) =>
            t.steps.some((s) => s instanceof AddMarkStep && s.mark.type === linkMark),
          );
          if (addedLink) {
            const { $from } = newState.selection;
            const block = $from.depth >= 1 ? $from.node(1) : null;
            const text = block?.textContent.trim() ?? '';
            // Auto-card only typed URLs: a paste has an active prompt (the menu
            // decides), and typed vendor URLs stay plain links.
            const promptActive = Boolean(linkPromptKey.getState(newState));
            const isEmbedVendorUrl = Object.values(linkEmbedVendors).some((vendor) => vendor.match(text));
            if (block && isSingleUrl(text) && blockHasLink(block, linkMark) && !promptActive && !isEmbedVendorUrl) {
              tr = insertCardAfterBlock(tr, type, text, $from);
            }
          }

          return tr.docChanged ? tr : null;
        },
        props: {
          handlePaste: (view, event) => {
            if (!onFetch) return false;
            // Position math below assumes a text selection; on a node selection
            // (e.g. a just-inserted card) let ProseMirror's default paste run.
            if (!(view.state.selection instanceof TextSelection)) return false;

            const text = event.clipboardData?.getData('text/plain')?.trim();
            if (!text) return false;

            const tokens = urlTokens(text);
            if (tokens.length === 0) return false;

            const { state } = view;
            const { from, to } = state.selection;
            const linkMark = state.schema.marks.link;

            let tr = state.tr.insertText(text, from, to);
            if (linkMark) {
              for (const { offset, url } of tokens) {
                tr = tr.addMark(from + offset, from + offset + url.length, linkMark.create({ href: url }));
              }
            }

            // A solo URL paste gets the prompt menu instead of an immediate
            // preview card: the default action is the vendor embed when the URL
            // matches one, the plain preview card otherwise. Without the
            // LinkPrompt extension there is no menu to resolve the prompt, so
            // fall back to the immediate card below.
            let promptSet = false;
            if (isSingleUrl(text) && linkPromptKey.get(state)) {
              const base = { from, to: from + text.length, active: 'previewOnly' as const };
              let meta: LinkPromptState | null = null;
              for (const [service, vendor] of Object.entries(linkEmbedVendors)) {
                const data = vendor.match(text);
                if (data) {
                  meta = { ...base, kind: 'embed' as const, service, data };
                  break;
                }
              }
              // At the cap a preview prompt couldn't deliver either card-producing
              // choice, so don't offer a menu that does nothing — keep the plain link.
              if (!meta && countLinkCards(state.doc, type) < MAX_LINK_CARDS) {
                meta = { ...base, kind: 'preview' as const };
              }
              if (meta) {
                tr = tr.setMeta(linkPromptKey, meta);
                promptSet = true;
              }
            }

            // The link mark is inclusive and autolink re-linkifies whole words,
            // so typing at the link's end would extend it. An unmarked separator
            // space keeps typing (and a repeated paste) outside the link.
            if (promptSet && linkMark) {
              const afterLink = from + text.length;
              if (!isPlainSpaceAt(tr, afterLink, linkMark)) {
                tr = tr.insertText(' ', afterLink);
                tr = tr.removeMark(afterLink, afterLink + 1, linkMark);
              }
              tr = tr.setSelection(TextSelection.create(tr.doc, afterLink + 1));
            }

            if (!promptSet) {
              for (const { url } of tokens) {
                tr = insertCardAfterBlock(tr, type, url, tr.selection.$from);
              }
            }

            view.dispatch(tr);
            return true;
          },
        },
      }),
    ];
  },
});
